import API from "./interceptor";

// ******************* Create a new product *******************
export const createProduct = async (productData) => {
  try {
    const response = await API.post('/api/products', productData);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to create product"
    );
  }
};

// ******************* Get all products *******************
export const getAllProducts = async (params = {}) => {
  try {
    const response = await API.get('/api/products', { params });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch products"
    );
  }
};

// ******************* Get product by ID *******************
export const getProductById = async (id) => {
  try {
    const response = await API.get(`/api/products/${id}`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch product"
    );
  }
};

// ******************* Get product by SKU *******************
export const getProductBySku = async (sku) => {
  try {
    const response = await API.get(`/api/products/sku/${sku}`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch product by SKU"
    );
  }
};

// ******************* Update product *******************
export const updateProduct = async (id, productData) => {
  try {
    const response = await API.put(`/api/products/${id}`, productData);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to update product"
    );
  }
};

// ******************* Update product stock *******************
export const updateProductStock = async (id, stockData) => {
  try {
    const response = await API.patch(`/api/products/${id}/stock`, stockData);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to update product stock"
    );
  }
};

// ******************* Delete product (soft delete) *******************
export const deleteProduct = async (id) => {
  try {
    const response = await API.delete(`/api/products/${id}`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to delete product"
    );
  }
};

// ******************* Activate product *******************
export const activateProduct = async (id) => {
  try {
    const response = await API.patch(`/api/products/${id}/activate`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to activate product"
    );
  }
};

// ******************* Get product statistics *******************
export const getProductStats = async () => {
  try {
    const response = await API.get('/api/products/stats');
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch product statistics"
    );
  }
};

// ******************* Get low stock products *******************
export const getLowStockProducts = async (threshold = 10) => {
  try {
    const response = await API.get('/api/products/low-stock', {
      params: { threshold }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch low stock products"
    );
  }
};

// ******************* Bulk operations on products *******************
export const bulkOperations = async (operation, productIds, updateData = null) => {
  try {
    const response = await API.post('/api/products/bulk', {
      operation,
      productIds,
      updateData
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to perform bulk operation"
    );
  }
};

// ******************* Fetch products by name (search) *******************
export const fetchProductsByName = async (name, params = {}) => {
  try {
    const response = await API.get('/api/products', {
      params: { search: name, ...params }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch products by name"
    );
  }
};

// ******************* Fetch products by supplier *******************
export const fetchProductsBySupplier = async (supplierId, params = {}) => {
  try {
    const response = await API.get('/api/products', {
      params: { supplier: supplierId, ...params }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch products by supplier"
    );
  }
};

// ******************* Fetch products by unit of measure *******************
export const fetchProductsByUnitOfMeasure = async (unitOfMeasure, params = {}) => {
  try {
    const response = await API.get('/api/products', {
      params: { unitOfMeasure, ...params }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch products by unit of measure"
    );
  }
};

// ******************* Fetch products by status *******************
export const fetchProductsByStatus = async (isActive, params = {}) => {
  try {
    const response = await API.get('/api/products', {
      params: { isActive, ...params }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch products by status"
    );
  }
};

// ******************* Fetch active products only *******************
export const fetchActiveProducts = async (params = {}) => {
  try {
    const response = await API.get('/api/products', {
      params: { isActive: true, ...params }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch active products"
    );
  }
};

// ******************* Fetch products with pagination and sorting *******************
export const fetchProductsWithPagination = async (page = 1, limit = 10, sortBy = 'createdAt', sortOrder = 'desc', params = {}) => {
  try {
    const response = await API.get('/api/products', {
      params: { 
        page, 
        limit, 
        sortBy, 
        sortOrder, 
        ...params 
      }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch products with pagination"
    );
  }
};

// ******************* Search products (comprehensive search) *******************
export const searchProducts = async (searchTerm, params = {}) => {
  try {
    const response = await API.get('/api/products', {
      params: { search: searchTerm, ...params }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to search products"
    );
  }
};

// ******************* Fetch products by price range *******************
export const fetchProductsByPriceRange = async (minPrice, maxPrice, priceType = 'sellingPrice', params = {}) => {
  try {
    // Note: This would require additional backend implementation for price range filtering
    const response = await API.get('/api/products', {
      params: { 
        minPrice, 
        maxPrice, 
        priceType,
        ...params 
      }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch products by price range"
    );
  }
};

// ******************* Add stock to product *******************
export const addProductStock = async (id, quantity) => {
  try {
    const response = await API.patch(`/api/products/${id}/stock`, {
      quantity,
      operation: 'add'
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to add product stock"
    );
  }
};

// ******************* Subtract stock from product *******************
export const subtractProductStock = async (id, quantity) => {
  try {
    const response = await API.patch(`/api/products/${id}/stock`, {
      quantity,
      operation: 'subtract'
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to subtract product stock"
    );
  }
};

// ******************* Set product stock *******************
export const setProductStock = async (id, quantity) => {
  try {
    const response = await API.patch(`/api/products/${id}/stock`, {
      quantity,
      operation: 'set'
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to set product stock"
    );
  }
};

// ******************* Bulk activate products *******************
export const bulkActivateProducts = async (productIds) => {
  try {
    const response = await API.post('/api/products/bulk', {
      operation: 'activate',
      productIds
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to activate products"
    );
  }
};

// ******************* Bulk deactivate products *******************
export const bulkDeactivateProducts = async (productIds) => {
  try {
    const response = await API.post('/api/products/bulk', {
      operation: 'deactivate',
      productIds
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to deactivate products"
    );
  }
};

// ******************* Bulk update product prices *******************
export const bulkUpdateProductPrices = async (productIds, priceData) => {
  try {
    const response = await API.post('/api/products/bulk', {
      operation: 'update_prices',
      productIds,
      updateData: priceData
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to update product prices"
    );
  }
};